'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { ApiResponse, PersonalizedTravelRecommendationResponse } from '@/lib/types/anthropic';
import { UserProfileManager } from '@/lib/user-profile-manager';
import type { SearchParams } from '@/app/flight-search/components/SearchInteractive';

interface ClaudeRecommendationsProps {
  searchParams: SearchParams;
}

export default function ClaudeRecommendations({ searchParams }: ClaudeRecommendationsProps) {
  const [recommendations, setRecommendations] = useState<PersonalizedTravelRecommendationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [personalizationScore, setPersonalizationScore] = useState(0);

  // Load personalization score on mount
  useEffect(() => {
    const profile = UserProfileManager.getProfile();
    if (profile) {
      const score = profile.travelHistory.length * 10 + 
                    (profile.preferences.preferredAirlines.length > 0 ? 20 : 0) +
                    (profile.bookingPatterns.frequentDestinations.length > 0 ? 20 : 0);
      setPersonalizationScore(Math.min(score, 100));
    }
  }, []);

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get API key from localStorage
      const apiKey = localStorage.getItem('anthropic_api_key');
      
      // Get user profile for personalization
      const userProfile = UserProfileManager.getProfile();
      
      const res = await fetch('/api/anthropic/travel-recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey ? { 'X-Anthropic-Key': apiKey } : {}),
        },
        body: JSON.stringify({
          searchParams,
          userProfile,
        }),
      });

      const data: ApiResponse<PersonalizedTravelRecommendationResponse> = await res.json();

      if (data.success) {
        setRecommendations(data.data);
        setPersonalizationScore(data.data.personalizationScore);
        setIsExpanded(true);
      } else {
        setError(data.error.message);
        
        if (!data.error.isInternal) {
          console.error('Error fetching recommendations:', data.error.message);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch recommendations.';
      setError(errorMessage);
      console.error('Error fetching recommendations:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'flight': return 'AirplaneIcon';
      case 'itinerary': return 'MapIcon';
      case 'tip': return 'LightBulbIcon';
      case 'alert': return 'BellAlertIcon';
      default: return 'SparklesIcon';
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl shadow-lg p-6 border-2 border-purple-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center shadow-md">
            <Icon name="SparklesIcon" size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-black text-gray-900">Claude AI Travel Assistant</h3>
            <p className="text-sm text-gray-600">Personalized recommendations powered by AI</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {personalizationScore > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 bg-white rounded-full border-2 border-purple-200">
              <Icon name="ChartBarIcon" size={16} className="text-purple-600" />
              <span className="text-sm font-bold text-purple-600">{personalizationScore}% Personalized</span>
            </div>
          )}
          {recommendations && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
            >
              <Icon 
                name={isExpanded ? "ChevronUpIcon" : "ChevronDownIcon"} 
                size={20} 
                className="text-gray-600"
              />
            </button>
          )}
        </div>
      </div>

      {/* Get Recommendations Button */}
      {!recommendations && !loading && (
        <button
          onClick={fetchRecommendations}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white font-bold rounded-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Icon name="SparklesIcon" size={20} />
          <span>Get Personalized AI Recommendations</span>
        </button>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center gap-3 py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          <p className="text-gray-600 font-medium">Claude is analyzing your travel profile...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="ExclamationTriangleIcon" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-red-900">Unable to fetch recommendations</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={fetchRecommendations}
            className="mt-3 text-sm font-bold text-red-600 hover:text-red-700 underline"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Recommendations Display */}
      {recommendations && isExpanded && (
        <div className="space-y-4 animate-fade-in">
          {/* Personalized Suggestions (Algorithm-based) */}
          {recommendations.personalizedSuggestions && recommendations.personalizedSuggestions.length > 0 && (
            <div className="bg-white rounded-lg p-4 shadow-sm border-2 border-purple-100">
              <h4 className="text-sm font-black text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="UserCircleIcon" size={18} className="text-purple-600" />
                Personalized Insights Based on Your Travel History
              </h4>
              <div className="space-y-3">
                {recommendations.personalizedSuggestions.map((suggestion, index) => (
                  <div 
                    key={index} 
                    className={`p-3 rounded-lg border-2 ${getPriorityColor(suggestion.priority)}`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon name={getTypeIcon(suggestion.type)} size={20} className="flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h5 className="text-sm font-bold">{suggestion.title}</h5>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-white/50 font-semibold uppercase">
                            {suggestion.priority}
                          </span>
                        </div>
                        <p className="text-sm mb-1">{suggestion.description}</p>
                        <p className="text-xs opacity-75 italic">{suggestion.reasoning}</p>
                        <div className="mt-2 flex items-center gap-1">
                          <div className="flex-1 bg-white/50 rounded-full h-1.5 overflow-hidden">
                            <div 
                              className="h-full bg-current" 
                              style={{ width: `${suggestion.confidence * 100}%` }}
                            />
                          </div>
                          <span className="text-xs font-semibold">{Math.round(suggestion.confidence * 100)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Flight Recommendations */}
          {recommendations.flightRecommendations && (
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="text-sm font-black text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="AirplaneIcon" size={18} className="text-blue-600" />
                Flight Booking Insights
              </h4>
              <div className="space-y-2 text-sm text-gray-700">
                {recommendations.flightRecommendations.bestTimeToBook && (
                  <div className="flex items-start gap-2">
                    <Icon name="CalendarIcon" size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>{recommendations.flightRecommendations.bestTimeToBook}</span>
                  </div>
                )}
                {recommendations.flightRecommendations.optimalDepartureTime && (
                  <div className="flex items-start gap-2">
                    <Icon name="ClockIcon" size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>{recommendations.flightRecommendations.optimalDepartureTime}</span>
                  </div>
                )}
                {recommendations.flightRecommendations.priceInsights && (
                  <div className="flex items-start gap-2">
                    <Icon name="CurrencyPoundIcon" size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>{recommendations.flightRecommendations.priceInsights}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Travel Tips */}
          {recommendations.tips.length > 0 && (
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="text-sm font-black text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="LightBulbIcon" size={18} className="text-yellow-500" />
                Travel Tips
              </h4>
              <ul className="space-y-2">
                {recommendations.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-purple-500 font-bold mt-0.5">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Itinerary Suggestions */}
          {recommendations.itinerarySuggestions.length > 0 && (
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="text-sm font-black text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="MapIcon" size={18} className="text-green-500" />
                Personalized Itinerary Ideas
              </h4>
              <div className="space-y-3">
                {recommendations.itinerarySuggestions.map((itinerary, index) => (
                  <div key={index} className="p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                    <h5 className="text-sm font-bold text-gray-900 mb-2">{itinerary.title}</h5>
                    <ul className="space-y-1 mb-2">
                      {itinerary.activities.map((activity: string, actIndex: number) => (
                        <li key={actIndex} className="flex items-start gap-2 text-xs text-gray-700">
                          <span className="text-green-500 font-bold mt-0.5">→</span>
                          <span>{activity}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="font-semibold text-green-700">💷 {itinerary.estimatedCost}</span>
                      <span className="text-gray-500">Best for: {itinerary.bestFor.join(', ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Full Recommendations */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <h4 className="text-sm font-black text-gray-900 mb-3 flex items-center gap-2">
              <Icon name="DocumentTextIcon" size={18} className="text-purple-500" />
              Complete AI Analysis
            </h4>
            <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {recommendations.recommendations}
            </div>
          </div>

          {/* Refresh Button */}
          <button
            onClick={fetchRecommendations}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white text-purple-600 font-bold rounded-lg hover:bg-purple-50 transition-all duration-200 border-2 border-purple-200"
          >
            <Icon name="ArrowPathIcon" size={18} />
            <span>Get New Recommendations</span>
          </button>
        </div>
      )}

      {/* Collapsed State */}
      {recommendations && !isExpanded && (
        <div className="text-center py-2">
          <button
            onClick={() => setIsExpanded(true)}
            className="text-sm font-bold text-purple-600 hover:text-purple-700 underline"
          >
            View Personalized Recommendations
          </button>
        </div>
      )}
    </div>
  );
}